import { useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { logger } from '@logger'
import {
  CANVAS_CHANNEL,
  CANVAS_VERSION,
  OUTBOUND,
  INBOUND,
  getCanvasUrl,
  buildInitPayload,
  roundToCanvasMessage,
} from '@core/canvas/canvasContract'
import {
  setCanvasProgress,
  setCanvasReady,
  setCanvasError,
  resetCanvas,
} from '@slices/uiSlice'
import { resolveMode } from '@core/appearance/resolveGameView'

const MODULE = 'Canvas Bridge'

// Safety valve: if the canvas never reports READY (misbehaving/old build), stop
// blocking the boot loader after this long so we never get stuck on loading.
const READY_TIMEOUT_MS = 20000

/**
 * Owns the game-canvas iframe: mounts the message listener BEFORE giving the
 * iframe a src (so the canvas can't BOOT before we're listening — otherwise the
 * handshake is lost and boot hangs), runs the NE_CRASH handshake, mirrors the
 * canvas' loading/ready state into the store, and forwards every round tick.
 *
 * Returns the props the <iframe> needs. The iframe is rendered WITHOUT a src;
 * the bridge assigns it imperatively only after the listener is attached.
 */
export const useCanvasBridge = () => {
  const dispatch = useDispatch()
  const store = useStore() // read fresh state inside handlers without stale closures

  const iframeRef = useRef(null)
  const readyRef = useRef(false)
  const readyTimeoutRef = useRef(null)

  const gameCode = useSelector((state) => state.launchParams.params.gameCode)
  const theme = useSelector((state) => state.launchParams.params.theme)
  const platform = useSelector((state) => state.layout.mode)
  // gameMode (NORMAL/MINI) is derived from the responsive layout mode, not a
  // launch param — the FE decides the play mode from the viewport.
  const gameMode = resolveMode(platform)
  const round = useSelector((state) => state.events.round)

  const post = (type, payload) => {
    const target = iframeRef.current?.contentWindow
    if (!target) return
    target.postMessage({ channel: CANVAS_CHANNEL, v: CANVAS_VERSION, type, ts: Date.now(), payload }, '*')
  }

  const sendInit = () =>
    post(OUTBOUND.INIT, buildInitPayload({ gameCode, gameMode, theme, platform }))

  // 1) Listener first, then set src.
  useEffect(() => {
    const url = getCanvasUrl(gameCode)
    if (!url) {
      logger.error(MODULE, 'No canvas URL configured for game', { gameCode })
      dispatch(setCanvasError(true)) // don't hang the boot loader
      return undefined
    }

    const onMessage = (event) => {
      const data = event?.data
      if (!data || data.channel !== CANVAS_CHANNEL || data.v !== CANVAS_VERSION) return

      switch (data.type) {
        case INBOUND.BOOT:
          sendInit()
          break
        case INBOUND.LOADING:
          dispatch(setCanvasProgress(Number(data.payload?.progress) || 0))
          break
        case INBOUND.READY: {
          readyRef.current = true
          clearTimeout(readyTimeoutRef.current)
          dispatch(setCanvasReady(true))
          // Push the current round immediately so the canvas isn't blank until
          // the next tick (it may become ready mid-phase).
          const msg = roundToCanvasMessage(store.getState().events.round)
          if (msg) post(msg.type, msg.payload)
          logger.success(MODULE, 'Canvas ready', data.payload)
          break
        }
        case INBOUND.ERROR:
          dispatch(setCanvasError(true))
          logger.error(MODULE, 'Canvas reported error', data.payload)
          break
        default:
          logger.warn(MODULE, 'Unknown canvas message', data)
      }
    }

    window.addEventListener('message', onMessage)
    // Assign the src imperatively (not via React state) so the canvas can only
    // start loading AFTER the listener above is attached — the handshake BOOT
    // can never arrive before we're ready to receive it.
    if (iframeRef.current) iframeRef.current.src = url

    readyTimeoutRef.current = setTimeout(() => {
      if (!readyRef.current) {
        logger.warn(MODULE, 'Canvas READY timeout — proceeding without it')
        dispatch(setCanvasError(true))
      }
    }, READY_TIMEOUT_MS)

    return () => {
      window.removeEventListener('message', onMessage)
      clearTimeout(readyTimeoutRef.current)
      dispatch(resetCanvas())
    }
    // Run once on mount: attach the listener, then assign src. Intentionally no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 2) Forward each round change to the canvas once it's ready.
  useEffect(() => {
    if (!readyRef.current || !round?.status) return
    const msg = roundToCanvasMessage(round)
    if (msg) post(msg.type, msg.payload)
  }, [round])

  const onLoad = () => sendInit() // backup INIT (idempotent on the canvas side)

  return { iframeRef, onLoad }
}
